import React, { useState, useEffect } from "react";
import { CommonTextArea, CommonInput } from "../common";
import { Faq } from "../../types/interfaces";
import useAxios from "../../hooks/useAxios";


type ModalProps = {
  show: boolean;
  handleModalOpenClose: (show: boolean) => void;
  faq?: Faq | null;
  getListData: () => void;
};

type FaqErrors = Partial<Record<keyof Faq, string>>;

const CreateUpdateFaq: React.FC<ModalProps> = ({ show, handleModalOpenClose, faq, getListData }) => {
  const axiosInstance = useAxios();
  const [errors, setErrors] = useState<Partial<FaqErrors>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<Faq>({
    _id: "",
	faq_type: 1,
    question: "",
    answer: "",
  });
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false);
  
  useEffect(() => {
    if (faq) {
      setFormData((prev) => ({
        ...prev,
        _id: faq._id ?? "",
        faq_type: faq.faq_type ?? 1,
        question: faq.question ?? "",
        answer: faq.answer ?? "",
      }));
    }
  }, [faq]);

  /*Handle Form Element Value Changed*/
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsFormChanged(true);
  };

  /*Validate Form*/
  const validate = (): boolean => {
    const newErrors: Partial<FaqErrors> = {};
	if (!formData.faq_type) {
      newErrors.faq_type = "Faq type is required";
    }
    if (!formData.question) {
      newErrors.question = "Question is required";
    }
    if (!formData.answer) {
      newErrors.answer = "Answer is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /*Submit Form*/
  const handleSubmit = async () => {
    if (validate()) {
      setIsSubmitting(true);
      try {
        const response: any = await axiosInstance({
          url: "faq/create-update",
          method: "POST",
		  data: formData,
        });
        //console.log('create/update response', response.data)
        //on success close the modal
        if (!response.error) {
		  getListData();	
          handleClose();
        }
      } catch (error: any) {
        //console.error('Error in api request:', error);
        if (error?.response?.data?.type === "validation_error") {
          let errors = error.response.data.message.errors; // API returned an error message
          setErrors(errors);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  const handleClose = () => {
    const newErrors: Partial<FaqErrors> = {};
    setErrors(newErrors);
    setFormData({ _id: "", faq_type: 1, question: "", answer: "" });
    handleModalOpenClose(false);
  };

  return (
    <>
	  <div
        className="modal fade show"
        tabIndex={-1}
        aria-modal="true"
        role="dialog"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable animate__animated animate__fadeInDown">
          <div className="modal-content position-relative">
            {/* Close button */}
            <button
              type="button"
              className="btn btn-icon btn-active-color-primary position-absolute"
              style={{ top: "10px", right: "10px", zIndex: 10 }}
              onClick={handleClose}
            >
              ✖
            </button>
			  <div className="modal-header">
				<h2>{faq ? "Update Faq" : "Create Faq"}</h2>
			  </div>
			  <div className="modal-body mx-xl-5">
				<form className="form">
				<div className="">
				  <label className="required form-label">Faq Type</label>
				  <select
					className={`form-select mb-2 ${errors.faq_type ? "is-invalid" : ""}`}
					name="faq_type"
					value={Number(formData.faq_type)}
					onChange={handleInputChange}
				  >
					<option value="">Set Category Type</option>
					<option key={1} value={1}>General</option>
					<option key={2} value={2}>Product</option>
					<option key={3} value={3}>Epuja</option>
					<option key={4} value={4}>Kundli</option>
					<option key={5} value={5}>Blog</option>
					<option key={6} value={6}>Yoga & Wellness</option>
				  </select>
				  {errors.faq_type && (
					  <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback">
						{typeof errors.faq_type === "string" ? errors.faq_type : ''}
					  </div>
				  )}
				  <div className="text-muted text-info fs-7">
					Set the faq type.
				  </div>
				</div>
				<div>
				  <CommonTextArea
					  show_label={true}
					  label="Question"
					  rows={2}
					  name="question"
					  placeholder="Enter Question"
					  value={formData.question}
					  onChange={handleInputChange}
					  is_required={true}
					  className=""
					  error={errors.question}
					  maxLength={200}
					/>
				</div>
				<div>
				  <CommonTextArea
					  show_label={true}
					  label="Answer"
					  rows={4}
					  name="answer"
					  placeholder="Enter Answer"
					  value={formData.answer}
					  onChange={handleInputChange}
					  is_required={true}
					  className=""
					  error={errors.answer}
					  maxLength={600}
					/>
                </div>
				<div className="border-top mb-5 mt-5" />
				  <div className="text-end">
					<button
					  type="button"
					  className="btn btn-light me-3"
					  onClick={handleClose}
					>
					  Cancel
					</button>
					{isSubmitting ? (
					  <button
						type="button"
						id="kt_modal_new_card_submit"
						className="btn btn-primary"
						disabled={true}
					  >
						<span
						  className="indicator-progress"
						  style={{ display: "block" }}
						>
						  Please wait...
						  <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
						</span>
					  </button>
					) : (
					  <button
						type="button"
						id="kt_modal_new_card_submit"
						className="btn btn-primary"
						onClick={handleSubmit}
					  >
						<span className="indicator-label">Submit</span>
					  </button>
					)}
				  </div>
				</form>
			  </div>
			</div>
         </div>
      </div>
	  <div className="modal-backdrop fade show" />
    </>
  );
};

export default CreateUpdateFaq;
